/**
 * Toast Component
 * 
 * Notification toast for success/error messages.
 * Includes both React component and standalone showAlert function.
 */

import React from "react";

// ============================================================
// REACT COMPONENT
// ============================================================

const styles = {
  toast: {
    position: "fixed",
    top: 16,
    right: 16,
    left: "auto",
    transform: "translateY(-120%)",
    background: "#f8fafc",
    color: "#0f172a",
    padding: "14px 18px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)",
    zIndex: 9999,
    minWidth: 320,
    maxWidth: 420,
    transition: "transform 0.35s ease, opacity 0.35s ease",
    opacity: 0,
  },
  toastShow: {
    transform: "translateY(0)",
    opacity: 1,
  },
  toastError: {
    borderLeft: "4px solid #ef4444",
  },
  toastSuccess: {
    borderLeft: "4px solid rgba(47, 214, 181, 1)",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  title: {
    display: "block",
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2,
    color: "#0f172a",
  },
  titleError: {
    color: "#ef4444",
  },
  message: {
    fontSize: 13,
    color: "#475569",
    whiteSpace: "pre-wrap",
  },
  closeBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: 16,
    color: "#94a3b8",
    padding: "2px 6px",
  },
};

export default function Toast({ show, message, isError = false, onClose }) {
  return (
    <div
      style={{
        ...styles.toast,
        ...(show ? styles.toastShow : {}),
        ...(isError ? styles.toastError : styles.toastSuccess),
      }}
    >
      <div style={styles.content}>
        <strong
          style={{
            ...styles.title,
            ...(isError ? styles.titleError : {}),
          }}
        >
          {isError ? "Submission incomplete" : "Submission received"}
        </strong>
        <div style={styles.message}>{message}</div>
      </div>

      {onClose && (
        <button
          type="button"
          style={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      )}
    </div>
  );
}

// ============================================================
// STANDALONE FUNCTIONS (for use outside React components)
// ============================================================

let toastContainer = null;
let toastId = 0;

/**
 * Show a toast alert notification
 * @param {string} message - The message to display
 * @param {string} type - Type: "success", "error", "warning", "info"
 * @param {number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
 */
export function showAlert(message, type = "info", duration = 4000) {
  // Create container if it doesn't exist
  if (!toastContainer) {
    toastContainer = document.createElement("div");
    toastContainer.id = "toast-standalone-container";
    toastContainer.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 999999;
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-width: 400px;
    `;
    document.body.appendChild(toastContainer);
  }

  const id = ++toastId;
  const colors = {
    success: { bg: "#22BA91", icon: "✓" },
    error: { bg: "#EF4444", icon: "✕" },
    warning: { bg: "#F59E0B", icon: "⚠" },
    info: { bg: "#141414", icon: "ℹ" },
  };
  const { bg, icon } = colors[type] || colors.info;

  const toastEl = document.createElement("div");
  toastEl.id = `toast-${id}`;
  toastEl.style.cssText = `
    background: white;
    border-radius: 10px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.15);
    padding: 14px 16px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    border: 1px solid #E5E7EB;
    animation: toastSlideIn 0.25s ease;
  `;

  toastEl.innerHTML = `
    <div style="
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: ${bg};
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 700;
      flex-shrink: 0;
    ">${icon}</div>
    <div style="
      flex: 1;
      font-size: 14px;
      font-weight: 500;
      color: #141414;
      line-height: 1.4;
    ">${message}</div>
    <button onclick="this.parentElement.remove()" style="
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      color: #6B7280;
      font-size: 18px;
      line-height: 1;
    ">×</button>
  `;

  toastContainer.appendChild(toastEl);

  // Add animation styles if not already added
  if (!document.getElementById("toast-styles")) {
    const style = document.createElement("style");
    style.id = "toast-styles";
    style.textContent = `
      @keyframes toastSlideIn {
        from { opacity: 0; transform: translateX(20px); }
        to { opacity: 1; transform: translateX(0); }
      }
    `;
    document.head.appendChild(style);
  }

  // Auto-remove
  if (duration > 0) {
    setTimeout(() => {
      if (toastEl.parentElement) {
        toastEl.style.opacity = "0";
        toastEl.style.transform = "translateX(20px)";
        toastEl.style.transition = "all 0.2s ease";
        setTimeout(() => toastEl.remove(), 200);
      }
    }, duration);
  }
}

/**
 * Show a confirmation dialog
 * @param {string} message - The message to display
 * @param {Function} onConfirm - Callback when confirmed
 * @param {Function} onCancel - Callback when cancelled
 * @param {Object} options - Additional options
 */
export function showConfirm(message, onConfirm, onCancel, options = {}) {
  const {
    confirmText = "Confirm",
    cancelText = "Cancel",
    confirmColor = "#EF4444",
    title = "Confirm Action",
  } = options;

  // Create overlay
  const overlay = document.createElement("div");
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999998;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.15s ease;
  `;

  // Create dialog
  const dialog = document.createElement("div");
  dialog.style.cssText = `
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    padding: 24px;
    max-width: 400px;
    width: 90%;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    animation: scaleIn 0.2s ease;
  `;

  dialog.innerHTML = `
    <h3 style="
      margin: 0 0 12px;
      font-size: 18px;
      font-weight: 600;
      color: #111827;
    ">${title}</h3>
    <p style="
      margin: 0 0 20px;
      font-size: 14px;
      color: #6B7280;
      line-height: 1.5;
    ">${message}</p>
    <div style="
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    ">
      <button id="confirm-cancel" style="
        padding: 10px 20px;
        border-radius: 8px;
        border: 1px solid #E5E7EB;
        background: white;
        color: #374151;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.15s;
      ">${cancelText}</button>
      <button id="confirm-ok" style="
        padding: 10px 20px;
        border-radius: 8px;
        border: none;
        background: ${confirmColor};
        color: white;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: opacity 0.15s;
      ">${confirmText}</button>
    </div>
  `;

  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  // Add animation styles
  if (!document.getElementById("confirm-styles")) {
    const style = document.createElement("style");
    style.id = "confirm-styles";
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes scaleIn {
        from { opacity: 0; transform: scale(0.95); }
        to { opacity: 1; transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
  }

  // Cleanup function
  const cleanup = () => {
    overlay.style.opacity = "0";
    overlay.style.transition = "opacity 0.15s ease";
    setTimeout(() => overlay.remove(), 150);
  };

  // Button handlers
  dialog.querySelector("#confirm-cancel").onclick = () => {
    cleanup();
    if (onCancel) onCancel();
  };

  dialog.querySelector("#confirm-ok").onclick = () => {
    cleanup();
    if (onConfirm) onConfirm();
  };

  // Escape key handler
  const handleEscape = (e) => {
    if (e.key === "Escape") {
      cleanup();
      if (onCancel) onCancel();
      document.removeEventListener("keydown", handleEscape);
    }
  };
  document.addEventListener("keydown", handleEscape);
}

// Named exports for compatibility
// Usage: import { showAlert, showConfirm } from "./components/ui/Toast";
